// Excepciones operativas — Portal Admin VentasLuga (F3 · pista Retail 🔵).
// Mockup: `surekeep/mockups/backoffice/portal-admin-vl-excepciones.html`.
//
// Una excepción es romper la regla para un caso puntual. Lo único que la separa de un fraude es el
// rastro de POR QUÉ se rompió y QUIÉN lo autorizó — por eso la justificación y la resolución son
// obligatorias, validadas con zod antes de enviar, y toda decisión se suma a la bitácora.
//
// El explorer de tres columnas es el del mockup: filtros · bandeja · ficha.
//
// ✂️ El tipo `autorizacion_tope` (venta sobre el tope diario) quedó fuera del corte.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { MasterDetailExplorer } from '@/shared/patterns/MasterDetail';
import { ScreenFrame, ScreenFrameBar } from '@/shared/patterns/ScreenFrame';
import { Button } from '@/kit';
import { useAuth } from '@/hooks/useAuth';
import { resolveOwnRetailerId } from '@/domains/retail/ventas/lib/retailerScope';
import { userMessageOf } from '@/api/errors';
import {
    useAddExcepcionNote,
    useApplyExcepcion,
    useCreateExcepcion,
    useExcepcionDetail,
    useExcepciones,
    useResolveExcepcion,
    useRetailDirectory,
    useReviewExcepcion,
} from './api/useExcepciones';
import { ExcepcionesFiltersPanel } from './components/ExcepcionesFiltersPanel';
import { ExcepcionesTable } from './components/ExcepcionesTable';
import {
    ExcepcionDetailPanel,
    parseDetailTab,
    type DetailTab,
} from './components/ExcepcionDetailPanel';
import { CreateExceptionModal, ResolveExceptionModal } from './components/ExcepcionForms';
import {
    DEFAULT_SORT,
    EMPTY_EXCEPCIONES_FILTERS,
    filterExceptions,
    requesterOf,
    sortExceptions,
    type ExcepcionesFilters,
    type SortState,
} from './lib/excepcionesFormat';
import { impactToCents, type CreateExceptionForm } from './lib/excepcionesSchema';

/**
 * 🔴 Las pantallas hacían `e instanceof Error ? e.message : …` y con eso un `TypeError` del propio
 * front llegaba crudo y en inglés a la cara del operador: se midió un toast diciendo **«Failed to
 * fetch»**. `userMessageOf` existe justo para eso. El detalle técnico no se pierde: sigue en la
 * consola y en Sentry, que es donde sirve.
 */
function asUserError(error: unknown): Error | null {
    if (!error) return null;
    return new Error(userMessageOf(error));
}

export function ExcepcionesPage() {
    const navigate = useNavigate();
    const { exc, tab, venta } = useSearch({ from: '/_backoffice/retail-admin/$section' });
    const { roleAssignments } = useAuth();
    const fixedRetailerId = resolveOwnRetailerId(roleAssignments);

    const [filters, setFiltersState] = useState<ExcepcionesFilters>(EMPTY_EXCEPCIONES_FILTERS);
    const [sort, setSortState] = useState<SortState>(DEFAULT_SORT);
    const [page, setPage] = useState(1);

    // Filtrar u ordenar cambia el universo de la tabla: quedarse en la página 7 muestra un vacío
    // que se lee como un error. Se vuelve a la primera, igual que el mockup. Va en el setter y no
    // en un efecto — un `setState` dentro de un efecto encadena un render de más.
    const setFilters = (next: ExcepcionesFilters) => {
        setFiltersState(next);
        setPage(1);
    };
    const setSort = (next: SortState) => {
        setSortState(next);
        setPage(1);
    };
    const [filtersCollapsed, setFiltersCollapsed] = useState(false);
    /**
     * `?venta=` abre el alta con esa venta ya puesta — el `openLevantar({ venta })` del mockup, que
     * es como se levanta una excepción desde la pantalla de Ventas sin volver a teclear el folio.
     *
     * Va como estado inicial perezoso y no en un efecto: un `setState` dentro de un efecto encadena
     * un render de más, y acá el valor se conoce desde el primer render.
     */
    const [createPrefill, setCreatePrefill] = useState<Partial<CreateExceptionForm> | null>(() =>
        venta === undefined ? null : { saleId: venta },
    );
    const [resolveApprove, setResolveApprove] = useState<boolean | null>(null);

    const list = useExcepciones();
    const detail = useExcepcionDetail(exc ?? null);
    const directory = useRetailDirectory(fixedRetailerId);
    const create = useCreateExcepcion();
    const resolve = useResolveExcepcion();
    const review = useReviewExcepcion();
    const apply = useApplyExcepcion();
    const addNote = useAddExcepcionNote();

    // Un solo indicador para las tres acciones del panel: nunca corren en paralelo (el estado del
    // caso solo admite una a la vez) y tres flags separados solo agregarían ruido a los botones.
    const panelBusy = review.isPending || apply.isPending || addNote.isPending;

    const rows = useMemo(() => list.data?.items ?? [], [list.data]);
    const { storeNameOf, userNameOf } = directory;

    const visibleRows = useMemo(
        () =>
            sortExceptions(
                filterExceptions(rows, filters, storeNameOf, userNameOf),
                sort,
                storeNameOf,
                userNameOf,
            ),
        [rows, filters, sort, storeNameOf, userNameOf],
    );

    /**
     * Las opciones de los combos de Tienda y Solicitado por salen de LAS FILAS, no del padrón.
     *
     * Un filtro que ofrece una tienda sin ni un caso deja la tabla vacía sin explicar por qué. El
     * alta sí usa el padrón completo: ahí la tienda todavía no tiene ninguna excepción y tiene que
     * poder elegirse igual.
     */
    const storeOptions = useMemo(() => {
        const map = new Map<string, string>();
        for (const row of rows) {
            if (row.storeId) map.set(row.storeId, storeNameOf(row.storeId) ?? row.storeId);
        }
        return [...map.entries()]
            .map(([value, label]) => ({ value, label }))
            .sort((a, b) => a.label.localeCompare(b.label, 'es-MX'));
    }, [rows, storeNameOf]);

    const requesterOptions = useMemo(() => {
        const map = new Map<string, string>();
        for (const row of rows) {
            if (row.requestedBy) map.set(row.requestedBy, requesterOf(row, userNameOf));
        }
        return [...map.entries()]
            .map(([value, label]) => ({ value, label }))
            .sort((a, b) => a.label.localeCompare(b.label, 'es-MX'));
    }, [rows, userNameOf]);

    const select = (exceptionId: string | null, nextTab?: DetailTab) => {
        void navigate({
            to: '/retail-admin/$section',
            params: { section: 'excepciones' },
            search: { exc: exceptionId ?? undefined, tab: nextTab ?? tab },
            replace: true,
        });
    };

    useEffect(() => {
        // 🔴 EL DEEP LINK A UN CASO QUE NO ESTÁ EN LA VISTA ATERRIZABA EN OTRO Y REESCRIBÍA LA URL.
        // Bastaba con que un filtro escondiera la fila —o con que el `?exc=` apuntara a un id que ya
        // no existe— para que la pantalla eligiera sola la primera y borrara el enlace compartido.
        // Compartir el link de una excepción es media razón de existir de una bandeja auditable.
        //
        // Ahora la autoselección corre SOLO cuando no hay ninguna selección. Un `?exc=` que no está
        // en la lista se respeta: la ficha lo pide por su cuenta y muestra su 404 o su error, que es
        // información, no un salto silencioso a otro expediente.
        if (list.isPending) return;
        if (!exc && visibleRows.length > 0) select(visibleRows[0].exceptionId);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- select se re-crea en cada render
    }, [exc, visibleRows, list.isPending]);

    return (
        <ScreenFrame>
            <ScreenFrameBar className="items-center justify-between gap-2 px-3 py-2">
                <p className="text-[11px] text-muted-foreground">
                    Casos que rompen la regla y necesitan autorización. Cada uno guarda su
                    justificación, su resolución y quién decidió.
                </p>
                <Button
                    size="sm"
                    leftIcon={<Plus className="size-3.5" aria-hidden />}
                    onClick={() => {
                        setCreatePrefill({});
                    }}
                >
                    Levantar excepción
                </Button>
            </ScreenFrameBar>

            <MasterDetailExplorer
                filtersCollapsed={filtersCollapsed}
                filters={
                    <ExcepcionesFiltersPanel
                        filters={filters}
                        onChange={setFilters}
                        stores={storeOptions}
                        requesters={requesterOptions}
                        collapsed={filtersCollapsed}
                        onToggleCollapsed={() => {
                            setFiltersCollapsed((v) => !v);
                        }}
                    />
                }
                list={
                    <ExcepcionesTable
                        rows={visibleRows}
                        counts={list.data?.counts}
                        activePill={filters.pill}
                        onPillChange={(pill) => {
                            setFilters({ ...filters, pill });
                        }}
                        sort={sort}
                        onSortChange={setSort}
                        page={page}
                        onPageChange={setPage}
                        selectedId={exc ?? null}
                        onSelect={(id) => {
                            select(id);
                        }}
                        storeNameOf={storeNameOf}
                        userNameOf={userNameOf}
                        isLoading={list.isPending}
                        error={asUserError(list.error)}
                        onRetry={() => {
                            void list.refetch();
                        }}
                    />
                }
                detail={
                    <ExcepcionDetailPanel
                        // Remonta la ficha al cambiar de caso: el borrador de la nota nace limpio
                        // por construcción, sin un efecto que lo borre un render tarde.
                        key={exc ?? 'sin-seleccion'}
                        exception={detail.data}
                        isLoading={detail.isPending}
                        error={asUserError(detail.error)}
                        onRetry={() => {
                            void detail.refetch();
                        }}
                        hasSelection={Boolean(exc)}
                        storeName={storeNameOf(detail.data?.storeId ?? null)}
                        requesterName={userNameOf(detail.data?.requestedBy ?? null)}
                        tab={parseDetailTab(tab)}
                        onTabChange={(next) => {
                            select(exc ?? null, next);
                        }}
                        onApprove={() => {
                            setResolveApprove(true);
                        }}
                        onReject={() => {
                            setResolveApprove(false);
                        }}
                        onDuplicate={() => {
                            const source = detail.data;
                            if (!source) return;
                            // «Duplicar» del mockup: un caso NUEVO con los datos base pre-llenados.
                            // No se copia el impacto ni la resolución — el caso nuevo es otro y su
                            // monto se captura de cero. El id jamás se copia.
                            setCreatePrefill({
                                type: source.type,
                                saleId: source.saleId ?? '',
                                storeId: source.storeId ?? '',
                                requestedBy: source.requestedBy ?? '',
                                justification: source.justification,
                                requestedAction: source.requestedAction ?? '',
                            });
                        }}
                        onReview={() => {
                            if (!exc) return;
                            review.mutate(
                                { exceptionId: exc },
                                {
                                    onSuccess: () => {
                                        toast.success('Caso tomado', {
                                            description:
                                                'Queda a tu nombre para que nadie más lo trabaje en paralelo.',
                                        });
                                    },
                                    onError: (e) => {
                                        toast.error(userMessageOf(e), {
                                            description:
                                                'Se recargó la ficha con el estado actual del caso.',
                                        });
                                    },
                                },
                            );
                        }}
                        onApply={() => {
                            if (!exc) return;
                            apply.mutate(
                                { exceptionId: exc },
                                {
                                    onSuccess: () => {
                                        toast.success('Marcada como aplicada', {
                                            description:
                                                'Queda constancia de que el ajuste ya se ejecutó. La venta no se modificó.',
                                        });
                                    },
                                    onError: (e) => {
                                        toast.error(userMessageOf(e), {
                                            description:
                                                'Se recargó la ficha con el estado actual del caso.',
                                        });
                                    },
                                },
                            );
                        }}
                        onAddNote={(body) => {
                            if (!exc) return;
                            addNote.mutate(
                                { exceptionId: exc, body },
                                {
                                    onSuccess: () => {
                                        toast.success('Nota agregada a la bitácora');
                                    },
                                    onError: (e) => {
                                        toast.error(userMessageOf(e));
                                    },
                                },
                            );
                        }}
                        isBusy={panelBusy}
                    />
                }
            />

            <CreateExceptionModal
                isOpen={createPrefill !== null}
                prefill={createPrefill ?? undefined}
                onClose={() => {
                    setCreatePrefill(null);
                }}
                isSubmitting={create.isPending}
                storeOptions={directory.storeOptions}
                storesUnavailable={directory.storesUnavailable}
                onSubmit={(values) => {
                    // Las claves opcionales se OMITEN cuando están vacías: el DTO del silo las
                    // declara opcionales y un `''` o un `null` explícito falla su validación.
                    create.mutate(
                        {
                            type: values.type,
                            ...(values.saleId.trim() ? { saleId: values.saleId.trim() } : {}),
                            ...(values.storeId.trim() ? { storeId: values.storeId.trim() } : {}),
                            ...(values.requestedBy.trim()
                                ? { requestedBy: values.requestedBy.trim() }
                                : {}),
                            justification: values.justification,
                            requestedAction: values.requestedAction,
                            ...(impactToCents(values.impactPesos) === undefined
                                ? {}
                                : { impactCents: impactToCents(values.impactPesos) }),
                        },
                        {
                            onSuccess: (created) => {
                                toast.success('Excepción levantada', {
                                    description:
                                        'Queda en estado Nueva: necesita revisión y aprobación antes de aplicarse.',
                                });
                                setCreatePrefill(null);
                                select(created.exceptionId);
                            },
                            onError: (e) => {
                                toast.error(userMessageOf(e));
                            },
                        },
                    );
                }}
            />

            <ResolveExceptionModal
                exception={resolveApprove === null ? null : (detail.data ?? null)}
                approve={resolveApprove ?? false}
                onClose={() => {
                    setResolveApprove(null);
                }}
                isSubmitting={resolve.isPending}
                onSubmit={(values) => {
                    if (!exc || resolveApprove === null) return;
                    resolve.mutate(
                        {
                            exceptionId: exc,
                            // 🔴 `decision`, no `approve`. El booleano se descartaba en silencio y
                            // el silo respondía 400: «decision must be one of the following values:
                            // APPROVE, REJECT».
                            request: {
                                decision: resolveApprove ? 'APPROVE' : 'REJECT',
                                resolution: values.resolution,
                            },
                        },
                        {
                            onSuccess: () => {
                                toast.success(
                                    resolveApprove ? 'Excepción aprobada' : 'Excepción rechazada',
                                    { description: 'La decisión quedó registrada en la bitácora.' },
                                );
                                setResolveApprove(null);
                            },
                            onError: (e) => {
                                toast.error(userMessageOf(e));
                            },
                        },
                    );
                }}
            />
        </ScreenFrame>
    );
}
